import { createContext, ReactNode, useContext, useState, useEffect, useMemo, useCallback } from "react";

interface SidebarContextValue {
  collapsed: boolean;
  setCollapsed: (v: boolean) => void;
  toggle: () => void;
  expandedWidth: number;
  collapsedWidth: number;
  width: number;
}

const EXPANDED = 260;
const COLLAPSED = 68;

const SidebarContext = createContext<SidebarContextValue | null>(null);

export function SidebarProvider({ children }: { children: ReactNode }) {
  const [collapsed, setCollapsed] = useState(window.innerWidth < 1024);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 1024) {
        setCollapsed(true);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const toggle = useCallback(() => setCollapsed(prev => !prev), []);

  const value = useMemo(() => ({
    collapsed,
    setCollapsed,
    toggle,
    expandedWidth: EXPANDED,
    collapsedWidth: COLLAPSED,
    width: collapsed ? COLLAPSED : EXPANDED,
  }), [collapsed, toggle]);
  return <SidebarContext.Provider value={value}>{children}</SidebarContext.Provider>;
}

export function useSidebar(): SidebarContextValue {
  const ctx = useContext(SidebarContext);
  if (!ctx) {
    // Safe fallback when sidebar isn't mounted (e.g. public pages)
    return {
      collapsed: false,
      setCollapsed: () => {},
      toggle: () => {},
      expandedWidth: EXPANDED,
      collapsedWidth: COLLAPSED,
      width: 0,
    };
  }
  return ctx;
}